import React, { useState } from 'react';
import t from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

import openEyeIcon from '../../../../assets/icons/referral/eye-icon.svg';
import closedEyeIcon from '../../../../assets/icons/referral/closed-eye-icon.svg';

import styles from './PasswordInput.scss';

const DefaultLabel = () => (
  <FormattedMessage
    id="passwordField.defaultLabel"
    defaultMessage="Enter your password"
    description="Default label preceding the password input"
  />
);

const DefaultPlaceholder = () => (
  <FormattedMessage
    id="passwordField.placeholder"
    defaultMessage="Min 8 characters"
    description="A placeholder shown when the password field is empty"
  />
);

/**
 * @param {string} passValue - current password value
 * @param {function} onChange
 * @param {?string} error - text of the error, ignored if empty string
 * @param {string | JSX.Element} [label]
 * @param {string | JSX.Element} [placeholder]
 * @param {string} [className]
 * @param {Object} [inputProps] - rest props applied to the <input/>
 * @returns {JSX.Element}
 */
const PasswordInput = ({
  passValue,
  onChange,
  error,
  label = (<DefaultLabel />),
  placeholder = (<DefaultPlaceholder />),
  className = '',
  ...inputProps
}) => {
  const intl = useIntl();

  const [isPasswordShown, setPasswordShown] = useState(false);
  const toggleInputType = () => setPasswordShown(!isPasswordShown);

  const togglePasswordButtonTitle = isPasswordShown
    ? intl.formatMessage({
      id: 'passwordField.hidePasswordButtonTitle',
      defaultMessage: 'Click to hide the password back',
      description: 'Tooltip of "Hide password" button'
    })
    : intl.formatMessage({
      id: 'passwordField.showPasswordButtonTitle',
      defaultMessage: 'Click to show the password',
      description: 'Tooltip of "Show password" button'
    });

  return (
    <label className={`${styles.label} ${error ? styles.error : ''} ${className}`}>
      {label}

      <div className={styles.inputWrapper}>
        {isPasswordShown
          ? (
            <input
              id="password-input-field"
              value={passValue}
              onChange={onChange}
              placeholder={placeholder}
              name="password"
              type="text"
              {...inputProps}
            />
          )
          : (
            <input
              id="password-input-field"
              value={passValue}
              onChange={onChange}
              placeholder={placeholder}
              name="password"
              type="password"
              {...inputProps}
            />
          )}

        <button
          onClick={toggleInputType}
          type="button"
          tabIndex="-1"
          className={styles.visibilityToggleButton}
          title={togglePasswordButtonTitle}
        >
          <img src={isPasswordShown ? closedEyeIcon : openEyeIcon } width="16" height="16" alt="" />
        </button>
      </div>

      <div className={styles.errorText}>{error}</div>

    </label>
  );
};

PasswordInput.propTypes = {
  passValue: t.string.isRequired,
  onChange: t.func.isRequired,
  error: t.string,
  emailValue: t.string,
  label: t.string,
  showForgotPassword: t.bool,
  hideEmptyError: t.bool
};

export default PasswordInput;
